import { MouseEvent, ReactNode, useCallback } from 'react';

import {
  Stack,
  StackProps,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import { removeTheseProps } from '@/utils';

type ContainerProps = StackProps & {
  hasLabel: boolean;
};

const Container = styled(Stack, {
  shouldForwardProp: removeTheseProps<ContainerProps>('hasLabel'),
})<ContainerProps>(({ hasLabel }) => ({
  height: hasLabel ? 56 : 'auto',
  position: 'relative',
  justifyContent: 'flex-end',
  color: '#202227',
}));

const StyledTypography = styled(Typography)({
  position: 'absolute',
  top: 0,
  left: 0,
  fontSize: 13,
  fontWeight: 700,
  color: 'inherit',
});

type StyledToggleButtonGroupProps = ToggleButtonGroupProps & {
  fullWidth?: boolean;
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
  shouldForwardProp:
    removeTheseProps<StyledToggleButtonGroupProps>('fullWidth'),
})<StyledToggleButtonGroupProps>(({ fullWidth }) => ({
  height: 36,
  width: fullWidth ? '100%' : 'max-content',
  padding: 4,
  borderRadius: 6,
  backgroundColor: '#E8EAEC',
}));

const StyledToggleButton = styled(ToggleButton)({
  fontSize: 13,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '4px !important',
  color: '#202227',
  fontWeight: 400,
  transition: 'all 200ms',
  '&.Mui-selected': {
    backgroundColor: '#FFF',
    fontWeight: 600,
    color: '#202227',
    '&:hover': {
      backgroundColor: '#FFF',
    },
  },
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    border: 'none',
  },
  textTransform: 'unset',
});

type Option<Value> = {
  value: Value;
  label: ReactNode;
};

type FullTextToggleButtonProps<Value extends string | number | boolean | null> =
  {
    value: Value;
    label?: ReactNode;
    options: [Option<Value>, ...Option<Value>[]];
    fullWidth?: boolean;
    ContainerProps?: StackProps;
    TitleProps?: TypographyProps;
    ToggleButtonGroupProps?: Omit<ToggleButtonGroupProps, 'value' | 'onChange'>;
    ToggleButtonProps?: Omit<ToggleButtonProps, 'value'>;
  } & (
    | {
        enforceValue?: true;
        onChange: (
          event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
          value: Value,
        ) => void;
      }
    | {
        enforceValue: false;
        onChange: (
          event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
          value: Value,
        ) => void;
      }
  );

export const FullTextToggleButton = <
  Value extends string | number | boolean | null,
>({
  enforceValue = true,
  ...props
}: FullTextToggleButtonProps<Value>) => {
  const handleChange = useCallback(
    (
      event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
      value: Value,
    ) => {
      if (enforceValue && value === null) return;
      props.onChange && props.onChange(event, value);
    },
    [enforceValue, props],
  );

  const value =
    props.value === 'true'
      ? true
      : props.value === 'false'
        ? false
        : props.value;

  return (
    <Container hasLabel={Boolean(props.label)} {...props.ContainerProps}>
      {props.label ? (
        <StyledTypography {...props.TitleProps}>{props.label}</StyledTypography>
      ) : null}
      <StyledToggleButtonGroup
        exclusive
        fullWidth={props.fullWidth}
        {...props.ToggleButtonGroupProps}
        value={value}
        onChange={handleChange as unknown as ToggleButtonGroupProps['onChange']}
      >
        {props.options.map((option, index) => {
          return (
            <StyledToggleButton
              fullWidth={props.fullWidth}
              value={option.value ?? {}}
              key={index}
            >
              {option.label}
            </StyledToggleButton>
          );
        })}
      </StyledToggleButtonGroup>
    </Container>
  );
};
